import { computed, unref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { useSessionValues } from '@/composables/useSessionValues'
import { selectedAccommodationId } from '@/layouts/queries'
import AccommodationService from '@/services/accommodationService/AccommodationService'
import PropertyDetailsService from '@/services/PropertyDetailsService'

export const hasDescriptionValidationError = error => {
  const { httpStatus, fieldErrors } = error?.response?.data ?? {}
  return httpStatus === 400 && fieldErrors?.some(fieldError => fieldError.field === 'description')
}

export function usePlatformLanguages() {
  const { language } = useSessionValues()

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: ['platformLanguages', language],
    queryFn: () => PropertyDetailsService.fetchPlatformLanguages({ locale: unref(language) ?? 'en-US' }),
  })

  const platformLanguages = computed(() => data.value?.data?.data?.getPlatformLanguages ?? [])

  const simplifiedPlatformLanguages = computed(() => {
    const languageByCode = new Map()

    for (const platformLanguage of platformLanguages.value) {
      const { code, language } = platformLanguage.languageTag
      let languageCode = language

      // Chinese requires special handling due to the different variants of the language code.
      if (language === 'zh') {
        if (code.startsWith('zh-Hans')) languageCode = 'zh-Hans'
        else if (code.startsWith('zh-Hant')) languageCode = 'zh-Hant'
        else continue
      }

      if (languageByCode.has(languageCode)) continue

      languageByCode.set(languageCode, {
        language: languageCode,
        translatedLabel: platformLanguage.translatedLabel,
      })
    }

    return Array.from(languageByCode.values())
  })

  return {
    platformLanguages,
    simplifiedPlatformLanguages,
    isPlatformLanguagesLoading: isLoading,
    isPlatformLanguagesFetching: isFetching,
    isPlatformLanguagesError: isError,
  }
}

export function usePropertyDescriptionFields() {
  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: ['propertyDescriptions', selectedAccommodationId],
    enabled: computed(() => !!unref(selectedAccommodationId)),
    queryFn: () =>
      AccommodationService.fetchPropertyDescriptions({
        accommodationId: unref(selectedAccommodationId),
      }),
  })

  const propertyDescriptions = computed(() => {
    const descriptions = data.value?.data
    return Array.isArray(descriptions) ? descriptions : []
  })

  return {
    propertyDescriptions,
    isPropertyDescriptionsLoading: isLoading,
    isPropertyDescriptionsFetching: isFetching,
    isPropertyDescriptionsError: isError,
  }
}

export function useDeletePropertyDescription() {
  const queryClient = useQueryClient()
  const {
    isPending: isDeletePropertyDescriptionLoading,
    isSuccess: isDeletePropertyDescriptionSuccess,
    isError: isDeletePropertyDescriptionError,
    mutate: deletePropertyDescription,
  } = useMutation({
    mutationFn: ({ accommodationId, languageCode }) =>
      AccommodationService.deletePropertyDescription({ accommodationId, languageCode }),
    onSuccess: (_, { languageCode }) => {
      let shouldInvalidateDescriptions = false
      queryClient.setQueryData(['propertyDescriptions', selectedAccommodationId], currentData => {
        const currentDescriptions = Array.isArray(currentData?.data) ? currentData.data : []
        const hasExistingDescription = currentDescriptions.some(
          description => description.languageCode === languageCode
        )

        /* Under normal circumstances, this should never happen.
        If it does, we need to refetch the descriptions. Very defensive programming. */
        if (!hasExistingDescription) {
          shouldInvalidateDescriptions = true
          return currentData
        }

        return {
          ...currentData,
          data: currentDescriptions.filter(description => description.languageCode !== languageCode),
        }
      })

      if (shouldInvalidateDescriptions) {
        queryClient.invalidateQueries({ queryKey: ['propertyDescriptions'] })
      }
    },
  })

  return {
    isDeletePropertyDescriptionLoading,
    isDeletePropertyDescriptionSuccess,
    isDeletePropertyDescriptionError,
    deletePropertyDescription,
  }
}

export function useUpdatePropertyDescription() {
  const queryClient = useQueryClient()
  const {
    isPending: isUpdatePropertyDescriptionLoading,
    isSuccess: isUpdatePropertyDescriptionSuccess,
    isError: isUpdatePropertyDescriptionError,
    error: updatePropertyDescriptionError,
    mutate: updatePropertyDescription,
  } = useMutation({
    mutationFn: ({ accommodationId, languageCode, description }) =>
      AccommodationService.updatePropertyDescription({
        accommodationId,
        languageCode,
        body: { description },
      }),
    onSuccess: response => {
      let shouldInvalidateDescriptions = false
      queryClient.setQueryData(['propertyDescriptions', selectedAccommodationId], currentData => {
        const updatedDescription = response.data
        const currentDescriptions = Array.isArray(currentData?.data) ? currentData.data : []
        const hasExistingDescription = currentDescriptions.some(
          description => description.languageCode === updatedDescription.languageCode
        )

        /* Under normal circumstances, this should never happen.
        If it does, we need to refetch the descriptions. Very defensive programming. */
        if (!hasExistingDescription) {
          shouldInvalidateDescriptions = true
          return currentData
        }

        return {
          ...currentData,
          data: currentDescriptions.map(description =>
            description.languageCode === updatedDescription.languageCode ? updatedDescription : description
          ),
        }
      })

      if (shouldInvalidateDescriptions) {
        queryClient.invalidateQueries({ queryKey: ['propertyDescriptions'] })
      }
    },
  })
  const isDescriptionValidationError = computed(() =>
    hasDescriptionValidationError(updatePropertyDescriptionError.value)
  )

  return {
    isUpdatePropertyDescriptionLoading,
    isUpdatePropertyDescriptionSuccess,
    isUpdatePropertyDescriptionError,
    isDescriptionValidationError,
    updatePropertyDescription,
  }
}

export function useCreatePropertyDescription() {
  const queryClient = useQueryClient()
  const {
    isPending: isCreatePropertyDescriptionLoading,
    isSuccess: isCreatePropertyDescriptionSuccess,
    isError: isCreatePropertyDescriptionError,
    error: createPropertyDescriptionError,
    mutate: createPropertyDescription,
  } = useMutation({
    mutationFn: ({ accommodationId, languageCode, description }) =>
      AccommodationService.createPropertyDescription({
        accommodationId,
        languageCode,
        body: { description },
      }),
    onSuccess: response => {
      let shouldInvalidateDescriptions = false
      queryClient.setQueryData(['propertyDescriptions', selectedAccommodationId], currentData => {
        const createdDescription = response.data
        const currentDescriptions = Array.isArray(currentData?.data) ? currentData.data : []
        const hasExistingDescription = currentDescriptions.some(
          description => description.languageCode === createdDescription.languageCode
        )

        /* Create should only add new descriptions. If cache already has it, refetch instead of replacing locally. */
        if (hasExistingDescription) {
          shouldInvalidateDescriptions = true
          return currentData
        }

        return {
          ...currentData,
          data: [...currentDescriptions, createdDescription],
        }
      })

      if (shouldInvalidateDescriptions) {
        queryClient.invalidateQueries({ queryKey: ['propertyDescriptions'] })
      }
    },
  })
  const isDescriptionValidationError = computed(() =>
    hasDescriptionValidationError(createPropertyDescriptionError.value)
  )

  return {
    isCreatePropertyDescriptionLoading,
    isCreatePropertyDescriptionSuccess,
    isCreatePropertyDescriptionError,
    isDescriptionValidationError,
    createPropertyDescription,
  }
}
